import React, { useState, useEffect } from "react";
import { AssessmentTemplate, ExamAssignment, Section, Question, Trait } from "../../types";
import ExamTakingPage from "./ExamTakingPage";
import GlassCard from "../../components/ui/GlassCard";
import Button from "../../components/ui/Button";
import examService from "../../services/examService";

interface ExamData {
  assessmentTemplate: AssessmentTemplate;
  assignment: ExamAssignment;
  candidateId: string;
  sections: Section[];
  questions: Question[];
  traits: Trait[];
}

const ExamTakingPageWrapper: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [examData, setExamData] = useState<ExamData | null>(null);
  const [loadingStep, setLoadingStep] = useState<string>("Initializing...");

  useEffect(() => {
    const loadExamData = async () => {
      try {
        const assignmentId = localStorage.getItem("currentAssignmentId");
        
        if (!assignmentId) {
          setError("No exam session found. Please select an exam from the exams page.");
          setLoading(false);
          return;
        }

        setLoadingStep("Fetching assignment details...");
        const data = await examService.fetchExamData(assignmentId);
        
        if (!data) {
          setError("Unable to load exam data. Please check your connection and try again.");
          setLoading(false);
          return;
        }

        setLoadingStep("Loading questions...");
        
        if (data.questions.length === 0) {
          setError("This exam has no questions configured. Please contact your administrator.");
          setLoading(false);
          return;
        }

        setLoadingStep("Preparing exam...");
        setTimeout(() => {
          setExamData(data);
          setLoading(false);
        }, 500);
      } catch (err) {
        console.error("Error loading exam:", err);
        setError("An unexpected error occurred while loading the exam.");
        setLoading(false);
      }
    };

    loadExamData();
  }, []);

  const handleFinish = () => {
    localStorage.removeItem("currentAssignmentId");
    window.location.reload();
  };

  const handleGoBack = () => {
    localStorage.removeItem("currentAssignmentId");
    window.history.back();
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <GlassCard className="p-12 text-center">
          <div className="animate-spin text-4xl mb-3">⏳</div>
          <p className="text-gray-500 font-semibold">{loadingStep}</p>
          <p className="text-xs text-gray-400 mt-2">Please wait while we prepare your assessment</p>
        </GlassCard>
      </div>
    );
  }

  if (error || !examData) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <GlassCard className="p-12 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <p className="text-red-500 font-semibold text-lg">{error || "Exam not found"}</p>
          <p className="text-gray-500 text-sm mt-2">Please make sure you have a valid exam session</p>
          <div className="flex gap-3 justify-center mt-6">
            <Button onClick={handleGoBack}>Go Back</Button>
            <Button variant="secondary" onClick={() => window.location.href = "/"}>
              Go to Dashboard
            </Button>
          </div>
        </GlassCard>
      </div>
    );
  }

  return (
    <ExamTakingPage
      assessmentTemplate={examData.assessmentTemplate}
      assignment={examData.assignment}
      candidateId={examData.candidateId}
      onFinish={handleFinish}
      preloadedSections={examData.sections}
      preloadedQuestions={examData.questions}
      preloadedTraits={examData.traits}
    />
  );
};

export default ExamTakingPageWrapper;